/**
 * 코드 운지법 계산기 (기타·우쿨렐레). tools/chords/voicings.py 와 같은 규칙 (2026-10).
 *
 * 코드 이름 → 구성음 → 지판에서 '그 음들만 내면서 손가락 4개(바레 포함)로 잡을 수 있는 자리'를 찾아 치기 쉬운 순서로.
 * DB(chords 테이블)에 사람이 등록한 운지법을 먼저 보여주고, 이 계산 결과를 뒤에 덧붙인다.
 * DB 에 없는 코드·키를 바꾼 코드·우쿨렐레도 모두 운지법이 나온다.
 *
 * frets: 낮은 줄 → 높은 줄 순서, null = 안 치는 줄(x), 0 = 개방현.
 * 기타 E2 A2 D3 G3 B3 E4, 우쿨렐레 G4(높은 G) C4 E4 A4.
 */
export type Frets = (number | null)[];

const PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
export const GUITAR = [40, 45, 50, 55, 59, 64];
export const UKULELE = [67, 60, 64, 69];

const QUALITY: Record<string, number[]> = {
  '': [0, 4, 7], M: [0, 4, 7], maj: [0, 4, 7], m: [0, 3, 7], min: [0, 3, 7],
  '7': [0, 4, 7, 10], M7: [0, 4, 7, 11], maj7: [0, 4, 7, 11], Maj7: [0, 4, 7, 11], '△7': [0, 4, 7, 11],
  m7: [0, 3, 7, 10], mM7: [0, 3, 7, 11], mmaj7: [0, 3, 7, 11], 'm(M7)': [0, 3, 7, 11],
  dim: [0, 3, 6], o: [0, 3, 6], dim7: [0, 3, 6, 9], o7: [0, 3, 6, 9],
  m7b5: [0, 3, 6, 10], 'ø': [0, 3, 6, 10], aug: [0, 4, 8], '+': [0, 4, 8], aug7: [0, 4, 8, 10], '7+': [0, 4, 8, 10],
  sus4: [0, 5, 7], sus: [0, 5, 7], sus2: [0, 2, 7], '7sus4': [0, 5, 7, 10], '7sus': [0, 5, 7, 10], '9sus4': [0, 5, 7, 10, 14],
  '6': [0, 4, 7, 9], m6: [0, 3, 7, 9], '69': [0, 4, 7, 9, 14], m69: [0, 3, 7, 9, 14],
  '9': [0, 4, 7, 10, 14], M9: [0, 4, 7, 11, 14], maj9: [0, 4, 7, 11, 14], m9: [0, 3, 7, 10, 14], mM9: [0, 3, 7, 11, 14],
  '11': [0, 7, 10, 14, 17], m11: [0, 3, 7, 10, 14, 17], M11: [0, 4, 7, 11, 14, 17],
  '13': [0, 4, 7, 10, 14, 21], m13: [0, 3, 7, 10, 14, 21], M13: [0, 4, 7, 11, 14, 21],
  '5': [0, 7], add9: [0, 4, 7, 14], add2: [0, 2, 4, 7], madd9: [0, 3, 7, 14], add4: [0, 4, 5, 7], add11: [0, 4, 7, 17],
  // 우리 악보에 자주 나오는 표기 (2026-10 전체 점검): A2 = sus2, FM6 = F6, C#Mm7 = C#mM7, D13sus4
  '2': [0, 2, 7], M6: [0, 4, 7, 9], Mm7: [0, 3, 7, 11], '13sus4': [0, 5, 7, 10, 14, 21], '13sus': [0, 5, 7, 10, 14, 21],
  '7sus2': [0, 2, 7, 10], M7sus4: [0, 5, 7, 11], m2: [0, 2, 3, 7], '6sus4': [0, 5, 7, 9],
};
const TENSION: Record<string, number> = {
  '9': 14, b9: 13, '#9': 15, '11': 17, '#11': 18, '13': 21, b13: 20, '6': 9, '2': 2, '4': 5,
  add9: 14, add2: 2, add4: 5, add11: 17, add13: 21, M7: 11, maj7: 11, '7': 10,
  '-9': 13, '+9': 15, '+11': 18, '-13': 20, b6: 8,
};

/** 악보·테이블 표기 → 비교용 (ᵇ/♭ → b, <sup>#</sup>/♯ → #, 공백 제거) */
export const normName = (s: string) =>
  (s || '').replace(/<sup>#<\/sup>/g, '#').replace(/[ᵇ♭]/g, 'b').replace(/♯/g, '#').replace(/\s/g, '');

/** 테이블 표기(플랫 = ᵇ)로: 'Bb7/Ab' → 'Bᵇ7/Aᵇ' */
export const tableName = (s: string) =>
  normName(s).replace(/(^|\/)([A-G])b/g, (_m, p, n) => `${p}${n}ᵇ`);

const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_NAMES = ['C', 'Dᵇ', 'D', 'Eᵇ', 'E', 'F', 'Gᵇ', 'G', 'Aᵇ', 'A', 'Bᵇ', 'B'];
/** 같은 코드의 다른 이름 (A# ↔ Bᵇ) — DB 에 한쪽 표기로만 등록된 경우 찾기용 */
export const enharmonic = (name: string): string | null => {
  const n = normName(name);
  const conv = (note: string) => {
    const m = note.match(/^([A-G])([#b]?)$/);
    if (!m || !m[2]) return note;
    const pc = (PC[m[1]] + (m[2] === '#' ? 1 : -1) + 12) % 12;
    return m[2] === '#' ? FLAT_NAMES[pc] : SHARP_NAMES[pc];
  };
  const m = n.match(/^([A-G][#b]?)(.*?)(?:\/([A-G][#b]?))?$/);
  if (!m) return null;
  const out = conv(m[1]) + m[2] + (m[3] ? '/' + conv(m[3]) : '');
  const t = tableName(out);
  return t === tableName(n) ? null : t;
};

export interface Parsed { root: number; pcs: Set<number>; bass: number; optional: Set<number> }

const notePc = (letter: string, acc: string) => (PC[letter] + (acc === '#' ? 1 : acc === 'b' ? -1 : 0) + 12) % 12;

export const parseChord = (name: string): Parsed | null => {
  const s = normName(name);
  if (!s || s.startsWith('/') || /N\.?C/i.test(s)) return null;
  const m = s.match(/^([A-G])([#b]?)(.*)$/);
  if (!m) return null;
  const root = notePc(m[1], m[2]);
  // '6/9' 는 분수 코드가 아니라 6·9 화음 (D6/9). 꼬리 쉼표 'C7#11,b9' 도 허용
  let rest = m[3].replace(/6\/9/, '69');
  let bass = root;
  const slash = rest.lastIndexOf('/');
  if (slash >= 0) {
    const b = rest.slice(slash + 1).match(/^([A-G])([#b]?)$/);
    if (!b) return null;
    bass = notePc(b[1], b[2]);
    rest = rest.slice(0, slash);
  }
  let tensions: string[] = [];
  const reT = /\(([^)]*)\)/g;
  let mt: RegExpExecArray | null;
  while ((mt = reT.exec(rest)) !== null) tensions.push(mt[1]);
  let qual = rest.replace(/\([^)]*\)/g, '').replace(/,/g, '');
  const addTail = qual.match(/^(.*?)(add\d+)$/);
  if (addTail && addTail[1] in QUALITY && addTail[1] !== '') {
    qual = addTail[1];
    tensions.push(addTail[2]);
  }
  if (qual === 'm7' && tensions.some((t) => ['b5', '-5'].includes(t.replace(',', '')))) {
    qual = 'm7b5';
    tensions = tensions.filter((t) => !['b5', '-5'].includes(t.replace(',', '')));
  }
  if (!(qual in QUALITY)) {
    const mm = qual.match(/^(.*?)((?:[b#+-]?(?:5|9|11|13))+)$/);
    if (mm && mm[1] in QUALITY) {
      qual = mm[1];
      tensions = tensions.concat(mm[2].match(/[b#+-]?(?:5|9|11|13)/g) || []);
    } else return null;
  }
  const ivs = new Set(QUALITY[qual]);
  for (const t of tensions) {
    const parts = t.split(/[,\s]+/).reduce((acc: string[], x) => acc.concat(x.match(/add\d+|omit\d|sus\d|[b#+-]?\d+|M7|maj7/g) || []), []);
    for (let part of parts) {
      part = part.trim();
      if (!part) continue;
      if (part === 'b5' || part === '-5') { ivs.delete(7); ivs.add(6); continue; }
      if (part === '#5' || part === '+5') { ivs.delete(7); ivs.add(8); continue; }
      if (part === 'omit3') { ivs.delete(3); ivs.delete(4); continue; }
      if (part === 'omit5') { ivs.delete(7); continue; }
      if (part === 'sus4') { ivs.delete(3); ivs.delete(4); ivs.add(5); continue; }
      if (part === 'sus2') { ivs.delete(3); ivs.delete(4); ivs.add(2); continue; }
      if (!(part in TENSION)) return null;
      const v = TENSION[part];
      if ([13, 14, 15].includes(v) && !ivs.has(10) && !ivs.has(11) && ['9', 'b9', '#9', '11', '13', 'b13'].includes(part)
        && qual !== '6' && qual !== 'm6') ivs.add(10);
      ivs.add(v);
    }
  }
  const pcs = new Set(Array.from(ivs).map((i) => (root + i) % 12));
  const optional = new Set<number>();
  if (ivs.has(7)) optional.add((root + 7) % 12);
  if (ivs.size >= 5 && ivs.has(17)) optional.add((root + 17) % 12);
  return { root, pcs, bass, optional };
};

const fingerCount = (frets: Frets): [number, boolean] => {
  const pressed = frets.map((f, i) => [i, f] as [number, number | null]).filter(([, f]) => f !== null && (f as number) > 0) as [number, number][];
  if (!pressed.length) return [0, false];
  const lo = Math.min(...pressed.map(([, f]) => f));
  const atLo = pressed.filter(([, f]) => f === lo).map(([i]) => i);
  let barre = false;
  if (atLo.length >= 2) {
    barre = true;
    for (let k = atLo[0]; k <= atLo[atLo.length - 1]; k++) if (frets[k] === null || (frets[k] as number) < lo) barre = false;
  }
  return [barre ? 1 + pressed.filter(([, f]) => f > lo).length : pressed.length, barre];
};

const similar = (a: Frets, b: Frets) => a.filter((x, i) => x === b[i]).length >= a.length - 1;

// 교본 표준 바레 모양 (r = 근음 칸). 6번 줄 근음(E형)·5번 줄 근음(A형). 가장 많이 쓰는 모양이라 1순위로
const SHAPES: Record<string, ((r: number) => Frets)[]> = {
  maj: [(r) => [r, r + 2, r + 2, r + 1, r, r], (r) => [null, r, r + 2, r + 2, r + 2, r]],
  min: [(r) => [r, r + 2, r + 2, r, r, r], (r) => [null, r, r + 2, r + 2, r + 1, r]],
  '7': [(r) => [r, r + 2, r, r + 1, r, r], (r) => [null, r, r + 2, r, r + 2, r]],
  m7: [(r) => [r, r + 2, r, r, r, r], (r) => [null, r, r + 2, r, r + 1, r]],
  maj7: [(r) => [null, r, r + 2, r + 1, r + 2, r]],
  sus4: [(r) => [r, r + 2, r + 2, r + 2, r, r], (r) => [null, r, r + 2, r + 2, r + 3, r]],
};
const shapeKind = (p: Parsed): string | null => {
  const iv = Array.from(p.pcs).map((x) => (x - p.root + 12) % 12).sort((a, b) => a - b).join(',');
  return ({ '0,4,7': 'maj', '0,3,7': 'min', '0,4,7,10': '7', '0,3,7,10': 'm7', '0,4,7,11': 'maj7', '0,5,7': 'sus4' } as Record<string, string>)[iv] || null;
};
const isStandardShape = (p: Parsed, f: Frets) => {
  const kind = shapeKind(p);
  if (!kind || p.bass !== p.root) return false;
  return SHAPES[kind].some((mk) => {
    for (let r = 0; r <= 12; r++) {
      const t = mk(r);
      if (t.every((x, i) => x === f[i])) return true;
    }
    return false;
  });
};

/**
 * 운지법 점수 (낮을수록 많이 쓰는 모양). 코드에 맞지 않거나 잡을 수 없으면 null.
 * 사람이 등록한 운지법과 계산한 운지법을 같은 기준으로 줄 세우는 데 쓴다.
 */
export const scoreVoicing = (p: Parsed, f: Frets, tuning: number[], name: string): number | null => {
  const guitar = tuning.length === 6;
  const n = tuning.length;
  const required = Array.from(p.pcs).filter((x) => !p.optional.has(x));
  const allowed = new Set([...Array.from(p.pcs), p.bass]);
  const played: number[] = [];
  f.forEach((x, i) => { if (x !== null) played.push(i); });
  if (played.some((i) => !allowed.has((tuning[i] + (f[i] as number)) % 12))) return null;
  if (guitar) {
    if (played.length < 4) return null;
    if (played[played.length - 1] - played[0] + 1 !== played.length) return null;
    if (played[played.length - 1] < n - 2) return null;
    if ((tuning[played[0]] + (f[played[0]] as number)) % 12 !== p.bass) return null;
    if (!p.pcs.has(p.bass) && played.filter((i) => (tuning[i] + (f[i] as number)) % 12 === p.bass).length > 1) return null;
  }
  const got = new Set(played.map((i) => (tuning[i] + (f[i] as number)) % 12));
  if (required.some((r) => !got.has(r))) return null;
  const fretted = played.map((i) => f[i] as number).filter((x) => x > 0);
  if (fretted.length && Math.max(...fretted) - Math.min(...fretted) > 3) return null;
  const [fingers, barre] = fingerCount(f);
  if (fingers > 4) return null;
  const opens = played.filter((i) => f[i] === 0).length;
  if (fretted.length && Math.min(...fretted) >= 5 && opens) return null;
  const accidental = /^[A-G][#b]/.test(normName(name));
  const avg = fretted.length ? fretted.reduce((a, b) => a + b, 0) / fretted.length : 0;
  let score = avg + fingers * 0.5 + (barre ? 0.3 : 0) - played.length * 0.7
    - Array.from(got).filter((x) => p.optional.has(x)).length * 0.3;
  if (guitar && f[n - 1] === null) score += 1.5;
  // 샵·플랫 코드나 높은 자리의 복잡한 코드에 개방현을 섞는 건 어색 (자연음 코드의 1~3칸 개방 모양은 기본 코드라 제외)
  if (opens && fretted.length && (accidental || (required.length >= 4 && Math.max(...fretted) > 3))) score += 0.5 * opens;
  if (!fretted.length) score -= 1;
  // 교본 표준 모양(개방 E·A형 포함)은 대표 운지법. 샵·플랫 코드는 개방 코드가 드물어 바레 표준 모양을 더 우대
  if (guitar && isStandardShape(p, f)) score -= accidental ? 3.5 : 1.5;
  // 개방현 기본 코드(C·G·D·B7 모양 등): 1~3칸 안에서 개방현을 쓰는 자연음 코드
  if (guitar && !accidental && opens >= 1 && fretted.length && Math.max(...fretted) <= 3) score -= opens >= 2 ? 1.0 : 0.8;
  return score;
};

/** 운지법 찾기 → 많이 쓰는 순서 */
export const searchVoicings = (name: string, tuning: number[], maxResults = 6): Frets[] => {
  const p = parseChord(name);
  if (!p) return [];
  const guitar = tuning.length === 6;
  const n = tuning.length;
  const allowed = new Set([...Array.from(p.pcs), p.bass]);
  const best = new Map<string, [Frets, number]>();
  for (let start = 0; start <= 12; start++) {
    const lo = Math.max(1, start);
    const opts: (number | null)[][] = [];
    for (let s = 0; s < n; s++) {
      const o: (number | null)[] = guitar ? [null, 0] : [0];
      for (let x = lo; x <= lo + 3; x++) o.push(x);
      opts.push(o.filter((x) => x === null || allowed.has((tuning[s] + (x as number)) % 12)));
    }
    const cur: Frets = new Array(n).fill(null);
    const rec = (s: number) => {
      if (s < n) {
        for (const x of opts[s]) { cur[s] = x; rec(s + 1); }
        return;
      }
      const score = scoreVoicing(p, cur, tuning, name);
      if (score === null) return;
      const key = cur.join(',');
      const prev = best.get(key);
      if (!prev || prev[1] > score) best.set(key, [cur.slice(), score]);
    };
    rec(0);
  }
  return pickDistinct(Array.from(best.values()), maxResults);
};

/** 점수 순으로 고르되 같은 자리의 비슷한 모양은 하나만 */
export const pickDistinct = (scored: [Frets, number][], maxResults: number): Frets[] => {
  const ranked = scored.slice().sort((a, b) => a[1] - b[1]).map(([f]) => f);
  const picked: Frets[] = [];
  const seen: [number, Frets][] = [];
  for (const f of ranked) {
    const fretted = f.filter((x) => x) as number[];
    const pos = fretted.length ? Math.min(...fretted) : 0;
    if (seen.some(([q, g]) => Math.abs(pos - q) <= 1 && similar(f, g))) continue;
    picked.push(f);
    seen.push([pos, f]);
    if (picked.length >= maxResults) break;
  }
  return picked;
};

/** frets → 화면이 쓰는 chords 테이블 행 모양 (line1 = 가장 높은 줄) */
export const toRow = (name: string, frets: Frets, type: 'guitar' | 'ukelele', id: string) => {
  const n = frets.length;
  const fretted = frets.filter((x) => x) as number[];
  const fr = fretted.length && Math.max(...fretted) > 4 ? Math.min(...fretted) : null;
  const row: any = { id, name, type, frNumber: fr, generated: true };
  for (let s = 0; s < n; s++) {
    const line = n - s;
    const f = frets[s];
    row[`allline${line}`] = f === null ? 0 : 1;
    row[`line${line}`] = f === null || f === 0 ? '[]' : `[${fr ? f - fr + 1 : f}]`;
  }
  for (let line = n + 1; line <= 6; line++) { row[`allline${line}`] = 0; row[`line${line}`] = '[]'; }
  return row;
};

/** chords 테이블 행 → frets */
export const fromRow = (r: any, type: string): Frets => {
  const n = type === 'guitar' ? 6 : 4;
  const fr = r.frNumber || 1;
  const out: Frets = [];
  for (let s = 0; s < n; s++) {
    const line = n - s;
    if (!r[`allline${line}`]) { out.push(null); continue; }
    let v: number[] = [];
    try { v = JSON.parse(r[`line${line}`] || '[]') || []; } catch (e) { v = []; }
    out.push(v.length ? Math.max(...v) + fr - 1 : 0);
  }
  return out;
};

/** 사람이 등록한 운지법 중 코드에 없는 음이 섞인 것(잘못 등록) 걸러내기 */
export const isValidRow = (r: any, type: string, p: Parsed | null) => {
  if (!p) return true;
  const tuning = type === 'guitar' ? GUITAR : UKULELE;
  const allowed = new Set([...Array.from(p.pcs), p.bass]);
  const f = fromRow(r, type);
  return f.every((x, i) => x === null || allowed.has((tuning[i] + x) % 12));
};
